import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Users, DollarSign, CalendarDays, FileText, TrendingUp, LogOut, Download } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

const COLORS = ["#002FA7", "#4B5563", "#94A3B8", "#CBD5E1", "#E2E8F0"];

function StatCard({ icon: Icon, label, value, sub, testId }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-6 flex flex-col" data-testid={testId}>
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</span>
        <Icon className="h-4 w-4 text-slate-400" strokeWidth={1.5} />
      </div>
      <span className="text-2xl sm:text-3xl tracking-tight font-medium text-slate-950" style={{ fontFamily: "Outfit, sans-serif" }}>
        {value}
      </span>
      {sub && <span className="mt-1 text-sm text-slate-500">{sub}</span>}
    </div>
  );
}

function formatKES(num) {
  if (num == null) return "KES 0";
  return `KES ${Number(num).toLocaleString("en-KE", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    API.get("/dashboard/stats")
      .then(({ data }) => setStats(data))
      .catch((err) => {
        console.error("Dashboard stats error:", err);
        setError(true);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <div className="animate-pulse text-sm text-slate-500">Loading dashboard...</div>
      </div>
    );
  }

  // Fallback: show empty dashboard instead of blank page
  const safeStats = stats || {
    employee_count: 0,
    total_payroll_runs: 0,
    pending_leaves: 0,
    latest_run: null,
    department_breakdown: [],
    monthly_payroll: [],
    total_payslips: 0,
    approved_leaves: 0,
    latest_payslip: null,
    employee: null,
  };

  const isEmployee = user?.role === "employee";

  if (isEmployee) {
    return (
      <div data-testid="employee-dashboard" className="space-y-8">
        <h1 className="text-2xl sm:text-3xl tracking-tight font-medium text-slate-950" style={{ fontFamily: "Outfit, sans-serif" }}>
          Welcome, {user?.name}
        </h1>
        {error && (
          <div className="bg-[#FFFBEB] border border-[#F59E0B] text-[#92400E] text-sm px-4 py-3 rounded-sm">
            Could not load dashboard data. Please refresh the page.
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
          <StatCard icon={FileText} label="Payslips" value={safeStats.total_payslips || 0} testId="stat-payslips" />
          <StatCard icon={CalendarDays} label="Pending Leave" value={safeStats.pending_leaves || 0} testId="stat-pending-leave" />
          <StatCard icon={CalendarDays} label="Approved Leave" value={safeStats.approved_leaves || 0} testId="stat-approved-leave" />
        </div>

        {/* Employee Actions: Give Notice & Download Clearance */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
          <GiveNoticeButton />
          <DownloadClearanceButton />
        </div>

        {safeStats.latest_payslip && (
          <div className="bg-white border border-slate-200 rounded-sm p-6">
            <h2 className="text-lg font-medium text-slate-900 mb-4">Latest Payslip - {safeStats.latest_payslip.month}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              <div><span className="text-slate-500 block">Gross Pay</span><span className="font-medium text-slate-950">{formatKES(safeStats.latest_payslip.gross_pay)}</span></div>
              <div><span className="text-slate-500 block">Deductions</span><span className="font-medium text-[#991B1B]">{formatKES(safeStats.latest_payslip.total_deductions)}</span></div>
              <div><span className="text-slate-500 block">Net Pay</span><span className="font-medium text-[#065F46]">{formatKES(safeStats.latest_payslip.net_pay)}</span></div>
              <div><span className="text-slate-500 block">PAYE</span><span className="font-medium text-slate-950">{formatKES(safeStats.latest_payslip.paye?.net_paye)}</span></div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Admin / HR dashboard
  const deptData = (safeStats.department_breakdown || []).map((d) => ({
    name: d.department,
    employees: d.count,
    salary: d.total_salary,
  }));

  const monthlyData = (safeStats.monthly_payroll || []).map((m) => ({
    month: m.month,
    gross: m.total_gross,
    net: m.total_net,
    deductions: m.total_deductions,
  })).reverse();

  return (
    <div data-testid="admin-dashboard" className="space-y-8">
      <h1 className="text-2xl sm:text-3xl tracking-tight font-medium text-slate-950" style={{ fontFamily: "Outfit, sans-serif" }}>
        Dashboard
      </h1>

      {error && (
        <div className="bg-[#FFFBEB] border border-[#F59E0B] text-[#92400E] text-sm px-4 py-3 rounded-sm">
          Could not load dashboard data. Please refresh the page.
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        <StatCard icon={Users} label="Active Employees" value={safeStats.employee_count || 0} testId="stat-employees" />
        <StatCard icon={DollarSign} label="Latest Gross" value={formatKES(safeStats.latest_run?.total_gross)} sub={safeStats.latest_run?.month} testId="stat-gross" />
        <StatCard icon={TrendingUp} label="Latest Net" value={formatKES(safeStats.latest_run?.total_net)} sub={safeStats.latest_run?.month} testId="stat-net" />
        <StatCard icon={CalendarDays} label="Pending Leave" value={safeStats.pending_leaves || 0} testId="stat-pending" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {/* Monthly payroll chart */}
        <div className="bg-white border border-slate-200 rounded-sm p-6">
          <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-6">Monthly Payroll Trend</h3>
          {monthlyData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#4B5563" }} />
                <YAxis tick={{ fontSize: 11, fill: "#4B5563" }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}K`} />
                <Tooltip formatter={(v) => formatKES(v)} />
                <Bar dataKey="gross" fill="#002FA7" name="Gross" radius={[2, 2, 0, 0]} />
                <Bar dataKey="net" fill="#94A3B8" name="Net" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-slate-500">No payroll data yet. Process your first payroll to see trends.</p>
          )}
        </div>

        {/* Department breakdown */}
        <div className="bg-white border border-slate-200 rounded-sm p-6">
          <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-6">Department Breakdown</h3>
          {deptData.length > 0 ? (
            <div className="flex items-center gap-6">
              <ResponsiveContainer width="50%" height={200}>
                <PieChart>
                  <Pie data={deptData} dataKey="employees" nameKey="name" cx="50%" cy="50%" outerRadius={80} strokeWidth={1} stroke="#fff">
                    {deptData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-2">
                {deptData.map((d, i) => (
                  <div key={d.name} className="flex items-center gap-2 text-sm">
                    <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                    <span className="text-slate-700 flex-1">{d.name}</span>
                    <span className="text-slate-500">{d.employees}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-500">No employees added yet.</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Give Notice Button Component ──────────────────────────────────────

function GiveNoticeButton() {
  const [open, setOpen] = useState(false);
  const [noticeEndDate, setNoticeEndDate] = useState("");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!noticeEndDate || !reason) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      await API.post("/employees/me/resign", {
        notice_end_date: noticeEndDate,
        reason,
        notes,
      });
      toast.success("Resignation submitted successfully. HR will review it.");
      setOpen(false);
      setNoticeEndDate("");
      setReason("");
      setNotes("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="bg-white border border-slate-200 rounded-sm p-6 hover:border-red-300 hover:bg-red-50 transition-colors text-left"
      >
        <div className="flex items-center gap-3 mb-2">
          <div className="h-10 w-10 rounded-sm bg-red-50 flex items-center justify-center">
            <LogOut className="h-5 w-5 text-red-600" />
          </div>
          <div>
            <h3 className="font-medium text-slate-900">Give Notice</h3>
            <p className="text-xs text-slate-500">Submit your resignation</p>
          </div>
        </div>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Submit Resignation</DialogTitle>
            <DialogDescription>
              Please provide your notice period end date and reason for leaving.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">
                Notice Period End Date *
              </label>
              <input
                type="date"
                value={noticeEndDate}
                onChange={(e) => setNoticeEndDate(e.target.value)}
                className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all text-sm"
                required
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">
                Reason for Leaving *
              </label>
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all text-sm"
                required
              >
                <option value="">Select a reason</option>
                <option value="Personal reasons">Personal reasons</option>
                <option value="Career change">Career change</option>
                <option value="Relocation">Relocation</option>
                <option value="Better opportunity">Better opportunity</option>
                <option value="Health reasons">Health reasons</option>
                <option value="Family reasons">Family reasons</option>
                <option value="Retirement">Retirement</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">
                Additional Notes (optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all text-sm"
                rows={3}
                placeholder="Any additional information..."
              />
            </div>
            <DialogFooter>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-900 rounded-sm px-5 py-2.5 font-medium transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="bg-red-600 hover:bg-red-700 text-white rounded-sm px-5 py-2.5 font-medium transition-colors text-sm"
              >
                {submitting ? "Submitting..." : "Submit Resignation"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── Download Clearance Button Component ───────────────────────────────

function DownloadClearanceButton() {
  const [templateExists, setTemplateExists] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    API.get("/admin/clearance-template/info")
      .then((res) => setTemplateExists(res.data?.exists || false))
      .catch(() => setTemplateExists(false))
      .finally(() => setChecking(false));
  }, []);

  const handleDownload = async () => {
    try {
      const response = await API.get("/admin/clearance-template", {
        responseType: "blob",
      });

      const contentDisposition = response.headers["content-disposition"] || "";
      const filenameMatch = contentDisposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)/i);
      const filename = filenameMatch ? decodeURIComponent(filenameMatch[1]) : "clearance_template.pdf";

      const blob = new Blob([response.data], { type: response.data.type || "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Unable to download clearance template. Please try again.");
    }
  };

  if (checking) return null;

  return (
    <button
      type="button"
      onClick={handleDownload}
      disabled={!templateExists}
      className={`bg-white border border-slate-200 rounded-sm p-6 text-left transition-colors ${
        templateExists
          ? "hover:border-[#002FA7]/30 hover:bg-[#002FA7]/5 cursor-pointer"
          : "opacity-50 cursor-not-allowed"
      }`}
    >
      <div className="flex items-center gap-3 mb-2">
        <div className="h-10 w-10 rounded-sm bg-[#002FA7]/10 flex items-center justify-center">
          <Download className="h-5 w-5 text-[#002FA7]" />
        </div>
        <div>
          <h3 className="font-medium text-slate-900">Download Clearance</h3>
          <p className="text-xs text-slate-500">
            {templateExists ? "Download clearance template" : "No template available yet"}
          </p>
        </div>
      </div>
    </button>
  );
}
